import React, { useState } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Message, myReaction } from "@/models/MessagingModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TooltipMenu } from "@/uiKit/atoms/TooltipMenu.tsx";

export const EMOJI_PALETTE = [
  "😮",
  "😂",
  "❤️",
  "👍",
  "😢",
  "🙏",
  "👏",
  "🎉",
  "🔥",
  "✅",
  "💪",
  "👌",
  "🤔",
  "🙌",
  "💯",
  "😅",
];

export type MessageReactionBarProps = {
  message: Message;
  meId: string;
  hovering: boolean;
  className?: string;
};

export const MessageReactionBar: React.FC<MessageReactionBarProps> = ({ message, meId, hovering, className }) => {
  const styles = useStyles(makeStyles);
  const toggleReaction = Queries.messaging.useToggleReaction();
  const [popupKey, setPopupKey] = useState(0);

  if (message.deleted) {
    return null;
  }

  const currentEmoji = myReaction(message.reactions, meId);

  const react = (emoji: string) => {
    toggleReaction.mutate({
      patientId: message.subjectId,
      messageId: message.id,
      emoji,
      currentEmoji,
    });
    setPopupKey(key => key + 1);
  };

  return (
    <TooltipMenu
      key={popupKey}
      id={`message-reaction-picker-${message.id}`}
      placement="bottom-end"
      closeOnInteract={false}
      className={cx(styles.container, { [styles.hidden]: !hovering }, className)}
      content={<ReactionPopup onPick={react} selectedEmoji={currentEmoji} />}
    >
      <Button
        variant="text"
        data-action="select-reaction"
        startIcon={<Icons.addReaction className={styles.triggerIcon} />}
        className={styles.trigger}
      />
    </TooltipMenu>
  );
};

type ReactionPopupProps = {
  onPick: (emoji: string) => void;
  selectedEmoji?: string;
};

const ReactionPopup: React.FC<ReactionPopupProps> = ({ onPick, selectedEmoji }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const emojiButton = (emoji: string, emojiClassName: string) => (
    <button
      key={emoji}
      type="button"
      data-action="messaging.reaction.toggle"
      data-emoji={emoji}
      aria-label={t("messaging.reactions.react", { emoji })}
      aria-pressed={selectedEmoji === emoji}
      className={cx(emojiClassName, { [styles.selected]: selectedEmoji === emoji })}
      onClick={() => onPick(emoji)}
    >
      {emoji}
    </button>
  );

  return (
    <div className={styles.grid} role="menu" aria-label={t("messaging.reactions.pickerLabel")}>
      {EMOJI_PALETTE.map(emoji => emojiButton(emoji, styles.cell))}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    alignSelf: "center",
    opacity: 1,
    transition: "opacity 0.3s ease-in-out",
  }),
  hidden: css({
    opacity: 0,
    pointerEvents: "none",
  }),
  trigger: css({
    borderRadius: "100%",
    width: FontSizes.h3,
    height: FontSizes.h3,
  }),
  triggerIcon: css({
    width: FontSizes.h5,
    height: FontSizes.h5,
  }),
  bar: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  }),
  grid: css({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
  }),
  cell: css({
    border: "none",
    background: "none",
    cursor: "pointer",
    padding: theme.spacing(3),
    fontSize: FontSizes.h4,
    lineHeight: 1,
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  }),
  selected: css({
    backgroundColor: theme.palette.grey[200],
  }),
});
